/**
 * SwipeDismissible — wraps an in-app banner / card / toast so the trainer
 * can flick it upward off-screen as well as tap the X. The wrapped node is
 * rendered inline (block) and stays accessible to assistive tech; the
 * dismiss callback fires once the swipe passes a small threshold.
 *
 * Usage:
 *   <SwipeDismissible onDismiss={() => setHidden(true)}>
 *     <Banner ... />
 *   </SwipeDismissible>
 *
 * The wrapper deliberately only tracks vertical pointer movement that
 * starts on the banner itself, so it never fights with normal page scroll:
 *  - horizontal-dominant motion releases the pointer back to the page
 *  - the first downward delta releases it too (so a trainer who starts
 *    inside the banner but scrolls down doesn't trigger a dismiss)
 *  - we only call setPointerCapture once we have at least 6px of
 *    upward travel, which keeps native click semantics intact for taps.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

const ACTIVATION_PX = 6;
const DISMISS_DISTANCE_PX = 72;
const DISMISS_VELOCITY_PX_PER_MS = 0.45;
const EXIT_DURATION_MS = 180;

export interface SwipeDismissibleHandle {
  /** Programmatic dismiss with the same animation. */
  dismiss: () => void;
}

export interface SwipeDismissibleProps {
  children: ReactNode;
  /** Fires after the exit animation finishes (or immediately on tap-X). */
  onDismiss: () => void;
  /** Disable swipe (X button still works). Defaults to false. */
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Forwarded testid so tests can target the wrapper. */
  "data-testid"?: string;
  /** Accessible label announced to screen readers. */
  ariaLabel?: string;
}

export const SwipeDismissible = forwardRef<SwipeDismissibleHandle, SwipeDismissibleProps>(
  function SwipeDismissible(
    { children, onDismiss, disabled, className, style, ariaLabel, ...rest },
    ref,
  ) {
    const testId = (rest as { "data-testid"?: string })["data-testid"];
    const [translateY, setTranslateY] = useState(0);
    const [exiting, setExiting] = useState(false);
    const stateRef = useRef<{
      startY: number;
      startX: number;
      startTime: number;
      lastY: number;
      lastTime: number;
      captured: boolean;
      released: boolean;
    } | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);

    const triggerDismiss = useCallback(() => {
      if (exiting) return;
      setExiting(true);
      // Slide the rest of the way out then notify the caller. The setTimeout
      // is tied to EXIT_DURATION_MS below, matching the CSS transition.
      window.setTimeout(() => {
        onDismiss();
      }, EXIT_DURATION_MS);
    }, [exiting, onDismiss]);

    useImperativeHandle(
      ref,
      () => ({
        dismiss: triggerDismiss,
      }),
      [triggerDismiss],
    );

    const handlePointerDown = useCallback(
      (event: ReactPointerEvent<HTMLDivElement>) => {
        if (disabled || exiting) return;
        // Only react to primary pointer (mouse left / single touch / pen).
        if (event.pointerType === "mouse" && event.button !== 0) return;
        const target = event.target as HTMLElement | null;
        // Don't hijack drags that start inside an interactive control —
        // taps on the X / Resume buttons should keep their native click.
        if (target && target.closest("input, textarea, select, [data-swipe-ignore]")) {
          return;
        }
        stateRef.current = {
          startY: event.clientY,
          startX: event.clientX,
          startTime: event.timeStamp,
          lastY: event.clientY,
          lastTime: event.timeStamp,
          captured: false,
          released: false,
        };
      },
      [disabled, exiting],
    );

    const handlePointerMove = useCallback(
      (event: ReactPointerEvent<HTMLDivElement>) => {
        const s = stateRef.current;
        if (!s || s.released) return;
        const dy = event.clientY - s.startY;
        const dx = event.clientX - s.startX;

        // Horizontal-dominant motion or downward motion → release control
        // back to the page so normal scrolling / horizontal swipes work.
        if (!s.captured) {
          if (Math.abs(dx) > Math.abs(dy) + 4) {
            s.released = true;
            return;
          }
          if (dy > 4) {
            s.released = true;
            return;
          }
          if (-dy < ACTIVATION_PX) {
            // Still inside the dead-zone; wait for more travel before
            // taking over the gesture.
            return;
          }
          // Activate: capture pointer so further moves come to us even if
          // the finger / cursor leaves the banner.
          try {
            (event.currentTarget as Element & {
              setPointerCapture: (id: number) => void;
            }).setPointerCapture(event.pointerId);
          } catch {
            // Some older browsers / synthetic events throw here; we can
            // still track movement without explicit capture.
          }
          s.captured = true;
        }

        s.lastY = event.clientY;
        s.lastTime = event.timeStamp;
        // Only translate upward; we never want this wrapper to drag down.
        setTranslateY(Math.min(0, dy));
        event.preventDefault();
      },
      [],
    );

    const finishGesture = useCallback(
      (event: ReactPointerEvent<HTMLDivElement>) => {
        const s = stateRef.current;
        stateRef.current = null;
        if (!s || !s.captured || s.released) {
          setTranslateY(0);
          return;
        }
        const dy = event.clientY - s.startY;
        const dt = Math.max(1, event.timeStamp - s.startTime);
        const velocity = -dy / dt; // upward = positive
        const distance = -dy;
        try {
          (event.currentTarget as Element & {
            releasePointerCapture: (id: number) => void;
          }).releasePointerCapture(event.pointerId);
        } catch {
          /* swallow */
        }
        if (distance >= DISMISS_DISTANCE_PX || velocity >= DISMISS_VELOCITY_PX_PER_MS) {
          triggerDismiss();
        } else {
          setTranslateY(0);
        }
      },
      [triggerDismiss],
    );

    // Read-only translate while exiting so we can animate to a fixed
    // off-screen position regardless of where the user released.
    const effectiveTranslate = exiting
      ? -Math.max(120, Math.abs(translateY) + 120)
      : translateY;

    // Reset translate when the banner remounts (children change wholesale).
    useEffect(() => {
      if (!exiting && translateY !== 0 && !stateRef.current) {
        setTranslateY(0);
      }
    }, [children, exiting, translateY]);

    return (
      <div
        ref={rootRef}
        className={className}
        data-testid={testId}
        aria-label={ariaLabel}
        style={{
          ...style,
          touchAction: "pan-x",
          transform: `translate3d(0, ${effectiveTranslate}px, 0)`,
          transition: stateRef.current
            ? "none"
            : exiting
            ? `transform ${EXIT_DURATION_MS}ms ease-out, opacity ${EXIT_DURATION_MS}ms ease-out`
            : "transform 160ms ease-out",
          opacity: exiting ? 0 : 1,
          willChange: "transform",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishGesture}
        onPointerCancel={finishGesture}
      >
        {children}
      </div>
    );
  },
);
